export const fmtMoney = (value) => {
  const n = Number(value);

  return Number.isFinite(n)
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";
};

export const fmtNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "";
};

export const validateAmortizationInputs = ({
  price,
  downPayment,
  years,
  apr,
  daysToFirstPayment,
  loanAmount,
}) => {
  if (
    price === 0 &&
    downPayment === 0 &&
    years === 0 &&
    apr === 0 &&
    daysToFirstPayment === 0
  ) {
    return "";
  }

  if (!Number.isFinite(price) || price <= 0) {
    return "Price must be greater than 0.";
  }

  if (!Number.isFinite(downPayment) || downPayment < 0) {
    return "Down payment must be 0 or greater.";
  }

  if (downPayment > price) {
    return "Down payment cannot be greater than price.";
  }

  if (!Number.isFinite(years) || years <= 0) {
    return "Years must be greater than 0.";
  }

  if (!Number.isFinite(apr) || apr < 0) {
    return "APR must be 0 or greater.";
  }

  if (!Number.isFinite(daysToFirstPayment) || daysToFirstPayment <= 0) {
    return "Days to first payment must be greater than 0.";
  }

  if (!Number.isFinite(loanAmount) || loanAmount <= 0) {
    return "Loan amount must be greater than 0.";
  }

  return "";
};

export const buildAmortizationSchedule = ({
  loanAmount,
  years,
  apr,
  daysToFirstPayment,
}) => {
  const rows = [];

  const totalPayments = Math.round(years * 12);
  const monthlyRate = apr / 100 / 12;
  const dailyRate = apr / 100 / 365;

  let monthlyPayment = NaN;

  if (monthlyRate === 0) {
    monthlyPayment = loanAmount / totalPayments;
  } else {
    monthlyPayment =
      (loanAmount * monthlyRate) /
      (1 - Math.pow(1 + monthlyRate, -totalPayments));
  }

  let beginningBalance = loanAmount;
  let cumulativeInterest = 0;
  let cumulativePrincipal = 0;

  for (let i = 1; i <= totalPayments; i += 1) {
    const daysInPeriod = i === 1 ? daysToFirstPayment : 30;

    let interest =
      i === 1
        ? beginningBalance * dailyRate * daysToFirstPayment
        : beginningBalance * monthlyRate;

    let payment = monthlyPayment;
    let principal = payment - interest;

    if (i === totalPayments || principal > beginningBalance) {
      principal = beginningBalance;
      payment = interest + principal;
    }

    const endingBalance = Math.max(0, beginningBalance - principal);

    cumulativeInterest += interest;
    cumulativePrincipal += principal;

    rows.push({
      paymentNumber: i,
      daysInPeriod,
      beginningBalance,
      payment,
      interest,
      principal,
      endingBalance,
      cumulativeInterest,
      cumulativePrincipal,
    });

    beginningBalance = endingBalance;
  }

  return {
    monthlyPayment,
    totalInterest: cumulativeInterest,
    totalPrincipal: cumulativePrincipal,
    totalPaid: cumulativeInterest + cumulativePrincipal,
    rows,
  };
};

/**
 * ***************************************************************
 * ADJUSTABLE AMORTIZATION SCHEDULE
 * ***************************************************************
 *
 * Same as buildAmortizationSchedule, but allows overriding
 * the days for each payment period using customDays[]
 *
 * customDays[i] -> overrides days for row i
 * customPayments[i] -> overrides payment for row i
 *
 * ***************************************************************
 */

export function buildAdjustableAmortizationSchedule({
  loanAmount,
  years,
  apr,
  daysToFirstPayment,
  customDays = [],
  customPayments = [],
}) {
  const rows = [];
  const numberOfPayments = years * 12;

  // daily interest rate (APR / 365)
  const dailyRate = apr / 100 / 365;

  // standard monthly rate for payment calculation
  const monthlyRate = apr / 100 / 12;

  // monthly payment formula
  const monthlyPayment =
    loanAmount *
    (monthlyRate /
      (1 - Math.pow(1 + monthlyRate, -numberOfPayments)));

  let balance = loanAmount;
  let cumulativeInterest = 0;
  let cumulativePrincipal = 0;

  for (let i = 0; i < numberOfPayments; i += 1) {
    /**
     * -----------------------------------------------------------
     * Determine days for this row
     * -----------------------------------------------------------
     * Priority:
     * 1. customDays[i] (user edited)
     * 2. first row uses daysToFirstPayment
     * 3. default = 30 days
     */
    const daysInPeriod =
      customDays[i] ?? (i === 0 ? daysToFirstPayment : 30);

    const beginningBalance = balance;

    // interest based on daily rate and days
    const interestPaid =
      beginningBalance * dailyRate * daysInPeriod;

    /**
     * Use custom payment if user edited the row.
     * Otherwise use the calculated monthly payment.
     */
    let paymentAmount = customPayments[i] ?? monthlyPayment;  

    /**
     * Prevent negative or too-small payment from creating
     * invalid principal results.
     */
    if (paymentAmount < interestPaid) {
      paymentAmount = interestPaid;
    }

    let principalPaid = paymentAmount - interestPaid;
    

    /**
     * -----------------------------------------------------------
     * Final payment adjustment
     * -----------------------------------------------------------
     * payment does not go below 0.
     */
    if (principalPaid > balance) {
      principalPaid = balance;
      paymentAmount = interestPaid + principalPaid;
    }

    balance -= principalPaid;
    cumulativeInterest += interestPaid;
    cumulativePrincipal += principalPaid;

    rows.push({
      paymentNumber: i + 1,
      daysInPeriod,
      beginningBalance,
      paymentAmount,
      interestPaid,
      principalPaid,
      endingBalance: balance < 0 ? 0 : balance, // ternary prevents negative balance due to rounding
      cumulativeInterest,
      cumulativePrincipal,
    });

    // stop early if loan is paid off
    if (balance <= 0) break;
  }

  return {
    monthlyPayment,
    totalInterest: cumulativeInterest,
    totalPrincipal: cumulativePrincipal,
    totalPaid: cumulativeInterest + cumulativePrincipal,
    rows,
  };
}